import {
  Form,
  Button,
  Input,
  FormInstance,
  Row,
  Col,
  Radio,
  Select,
  Modal,
  Tag,
} from "antd";
import dayjs from "dayjs";
import {
  dateTimeDisplayFormat,
} from "../../utils/DateAndTimeConvertor";

import AmountField from "../../components/AmountField";
import { useWatch } from "antd/es/form/Form";
import { numberValidation } from "../../utils/ValidationRules";
import DateTimePicker from "../../components/DateTimePicker";
import { combineDateAndTime, getNow } from "../../utils/DateFn";
import { useAPI } from "../../hooks/useAPI";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { InclusionData } from "../../models/event";
import { activityOptions } from "../../constants/distance.constant";
import SectionCard from "../../components/SectionCard";
import DescriptionArea from "../../components/DescriptionArea";
const TicketForm = ({
  showInlineForm,
  setShowInlineForm,
  editID,
  setEditID,
  onSubmit,
  onMarkSoldOut,
  showMarkSoldOut = false,
  isSoldOut = false,
  eventType,
  onCancel,
  disabledTime,
  eventStartDate,
  eventEndDate,
  eventStartTime,
  eventEndTime,
  currency,
  form,
  soldQty = 0,
}: {
  showInlineForm: boolean;
  setShowInlineForm: (value: boolean) => void;
  editID: string;
  setEditID: (value: string) => void;
  onSubmit: (values: any) => void;
  onMarkSoldOut?: () => void;
  showMarkSoldOut?: boolean;
  isSoldOut?: boolean;
  eventType?: string;
  onCancel?: () => void;
  disabledTime: string;
  eventStartDate?: string;
  eventEndDate?: string;
  eventStartTime?: string;
  eventEndTime?: string;
  currency?: string;
  form: FormInstance;
  soldQty?: number;
}) => {
  const quantity = useWatch("quantity", form);
  const max_trans = useWatch("max_per_transaction", form);
  const activityType = useWatch("activity_type", form);

  const isStandalone = !onCancel;

  const { response: inclusionsResponse } = useAPI<{
    status: boolean;
    data: { data: InclusionData[] };
  }>(ENDPOINTS.get_inclusion, showInlineForm || isStandalone, [], {
    method: "GET",
  });

  const { response: distancesResponse } = useAPI<{
    status: boolean;
    data: { label: string; value: string; activity_type: string }[];
  }>(ENDPOINTS.get_distances, showInlineForm || isStandalone, [], {
    method: "GET",
  });

  const distanceOptions = (distancesResponse?.data ?? [])
    .filter((d) => d.activity_type === activityType)
    .map((d) => ({ label: d.label, value: d.value }));

  const inclusionOptions =
    inclusionsResponse?.data?.data?.map((item) => ({
      label: item.name,
      value: item.id,
    })) ?? [];

  const formContent = (
    <Form
      onFinish={onSubmit}
      form={form}
      layout="vertical"
      className="max-w-2xl"
    >
      {/* ── Section 1: Ticket Basics ── */}
      <SectionCard
        title="Ticket Information"
        description="Name, category and activity type for this ticket"
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={eventType === "hybrid" ? 12 : 24}>
            <Form.Item
              name="name"
              label="Ticket Name"
              rules={[{ required: true, message: "Ticket name is required" }]}
            >
              <Input placeholder="e.g. Open Category, Elite, Junior" />
            </Form.Item>
          </Col>

          {eventType === "hybrid" && (
            <Col xs={24} sm={12}>
              <Form.Item
                name="event_type"
                label="Ticket Event Type"
                rules={[{ required: true, message: "Event type is required" }]}
              >
                <Radio.Group>
                  <Radio value="on_ground">On-Ground</Radio>
                  <Radio value="virtual">Virtual</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={12}>
            <Form.Item
              name="activity_type"
              label="Activity Type"
              rules={[{ required: true, message: "Activity type is required" }]}
              initialValue="running"
            >
              <Radio.Group
                onChange={() => form.setFieldValue("distance", undefined)}
              >
                {activityOptions.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="distance"
              label="Distance"
              rules={[{ required: true, message: "Distance is required" }]}
              style={{ marginBottom: 0 }}
            >
              <Select
                placeholder="Select distance"
                options={distanceOptions}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </SectionCard>

      {/* ── Section 2: Description ── */}
      <SectionCard
        title="Ticket Description"
        description="What participants should know about this ticket"
      >
        <DescriptionArea
          required={false}
          MAX_LENGTH={100}
          disabled={false}
          label={"Additional Information"}
        />
      </SectionCard>

      {/* ── Section 3: Eligibility ── */}
      <SectionCard
        title="Age Eligibility"
        description="Set the age range for participants buying this ticket"
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="min_age"
              label="Minimum Age"
              rules={numberValidation(
                "Minimum age is required",
                "",
                true,
                false,
                150,
                "Cannot exceed 150",
              )}
            >
              <Input
                type="number"
                min={0}
                style={{ width: "100%" }}
                placeholder="e.g. 16"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="max_age"
              label="Maximum Age (optional)"
              dependencies={["min_age"]}
              style={{ marginBottom: 0 }}
              rules={[
                ...numberValidation(
                  "Cannot exceed 150",
                  "",
                  false,
                  false,
                  150,
                  "Cannot exceed 150",
                ),
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (
                      !value ||
                      !getFieldValue("min_age") ||
                      Number(value) >= Number(getFieldValue("min_age"))
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Max age cannot be less than min age"),
                    );
                  },
                }),
              ]}
            >
              <Input
                type="number"
                min={0}
                style={{ width: "100%" }}
                placeholder="e.g. 65"
              />
            </Form.Item>
          </Col>
        </Row>
      </SectionCard>

      {/* ── Section 4: Inclusions ── */}
      <SectionCard
        title="Ticket Inclusions"
        description="Select what is included with this ticket (t-shirt, medal, etc.)"
      >
        <Form.Item
          name="inclusions"
          label="Inclusions"
          style={{ marginBottom: 0 }}
        >
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Select inclusions"
            options={inclusionOptions}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
      </SectionCard>

      {/* ── Section 5: Pricing & Capacity ── */}
      <SectionCard
        title="Pricing & Capacity"
        description="Set the ticket price, total availability, and per-order limits"
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <AmountField name="price" currency={currency} />
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="quantity"
              label="Total Available Quantity"
              rules={[
                ...numberValidation(
                  "Total available quantity is required",
                  "",
                  true,
                  false,
                  500000000,
                  "Cannot exceed 500,000,000",
                ),
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === "") {
                      return Promise.resolve();
                    }

                    const num = Number(value);

                    if (Number.isNaN(num) || num < 0) {
                      return Promise.reject("Value must be 0 or greater");
                    }

                    // 0 = sold out
                    if (num === 0) {
                      return Promise.resolve();
                    }

                    if (soldQty > 0 && num < soldQty) {
                      return Promise.reject(
                        `Cannot be less than sold quantity (${soldQty})`,
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                type="number"
                min={0}
                style={{ width: "100%" }}
                placeholder="e.g. 500 (0 = sold out)"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="min_per_transaction"
              label="Min tickets per order"
              initialValue={1}
              rules={numberValidation(
                "Minimum quantity is required",
                "",
                true,
                quantity > 0,
                quantity,
                "Cannot exceed total quantity",
                max_trans,
                "Cannot exceed max per order",
              )}
            >
              <Input
                type="number"
                min={1}
                style={{ width: "100%" }}
                placeholder="1"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="max_per_transaction"
              label="Max tickets per order"
              initialValue={5}
              style={{ marginBottom: 0 }}
              rules={numberValidation(
                "Cannot exceed total quantity",
                "",
                true,
                quantity > 0,
                quantity,
              )}
            >
              <Input
                type="number"
                min={1}
                style={{ width: "100%" }}
                placeholder="5"
              />
            </Form.Item>
          </Col>
        </Row>
      </SectionCard>

      {/* ── Section 6: Registration Window ── */}
      <SectionCard
        title="Registration Window"
        description="Define when registration opens and closes for this ticket"
      >
        {(eventStartDate || eventEndDate) && (
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: "8px 12px",
              marginBottom: 16,
              fontSize: 12,
              color: "#6b7280",
            }}
          >
            <span style={{ fontWeight: 600, color: "#374151" }}>Event: </span>
            {eventStartDate
              ? dateTimeDisplayFormat(eventStartDate, eventStartTime)
              : ""}
            {eventStartDate && eventEndDate ? " — " : ""}
            {eventEndDate ? dateTimeDisplayFormat(eventEndDate, eventEndTime) : ""}
          </div>
        )}

        <div className="flex justify-end mb-3">
          <Button
            size="small"
            onClick={() => {
              const now = dayjs();
              const values: Record<string, unknown> = {
                registration_start_date: now,
                registration_start_time: now.format("hh:mm A"),
              };
              // Only set start date/time; do not modify end date/time
              form.setFieldsValue(values);

              form
                .validateFields([
                  "registration_start_date",
                  "registration_start_time",
                  "registration_end_date",
                  "registration_end_time",
                  "_datetime_range_validator_registration_start_date_registration_start_time_registration_end_date_registration_end_time",
                ])
                .catch(() => undefined);
            }}
          >
            Set current time
          </Button>
        </div>

        <Row gutter={[16, 0]}>
          <DateTimePicker
            form={form}
            label={[
              "Registration Opens",
              "Time",
              "Registration Closes",
              "Time",
            ]}
            name={[
              "registration_start_date",
              "registration_start_time",
              "registration_end_date",
              "registration_end_time",
            ]}
            disabledBasedOn={disabledTime}
            minGapMinutes={0}
            bounds={{
              minDateTime: editID ? undefined : getNow(),
              maxDateTime: combineDateAndTime(eventEndDate, eventEndTime),
              isEdit: !!editID,
              savedStartDateTime: editID
                ? dayjs("1970-01-01")
                : undefined,
            }}
          />
        </Row>
      </SectionCard>

      <Form.Item name="currency" hidden initialValue={currency}>
        <Input />
      </Form.Item>

      {/* ── Footer Actions ── */}
      <div className="flex justify-end items-center gap-3 pt-4 pb-2">
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        {editID && isSoldOut && (
          <Tag color="red" className="!mr-auto">
            Sold Out
          </Tag>
        )}

        {editID && showMarkSoldOut && onMarkSoldOut && (
          <Button danger onClick={onMarkSoldOut}>
            Mark as Sold Out
          </Button>
        )}
        <Button type="primary" htmlType="submit">
          {editID ? "Update Ticket" : "Save Ticket"}
        </Button>
      </div>
    </Form>
  );

  if (isStandalone) {
    return (
      <div className="flex justify-center max-w-3xl mx-auto">{formContent}</div>
    );
  }

  return (
    <Modal
      open={showInlineForm}
      maskClosable={false}
      footer={null}
      width={720}
      onCancel={() => {
        setShowInlineForm(false);
        form.resetFields();
        setEditID("");
      }}
      style={{ marginTop: 24, marginBottom: 8 }}
      title={
        <span style={{ fontSize: 15, fontWeight: 600 }}>
          {editID ? "Edit Ticket" : "Add New Ticket"}
        </span>
      }
    >
      {formContent}
    </Modal>
  );
};

export default TicketForm;
